import React from 'react';
import LatexRenderer from './LatexRenderer';
var Latex = require('../utils/SafeLatex').default;

// Markdown-style pipe tables ("| $x$ | $y$ |" rows with a "| --- | --- |"
// separator) authored into math questionText / answerChoices render as a real
// data table instead of literal pipe text. Cells go through Latex so headers
// like $x$ keep rendering as math — the reason this is an HTML table and not
// a baked SVG like option.figure: KaTeX can't render inside an <svg>.

// A line is a table row when it is fully pipe-delimited. Math like $|x|$
// never qualifies: those lines start with $, not |.
const isRow = (line) => /^\s*\|.*\|\s*$/.test(line);
const isSep = (line) => /^\s*\|(?:\s*:?-{2,}:?\s*\|)+\s*$/.test(line);

export const hasPipeTable = (text) =>
  typeof text === 'string' && text.split('\n').some(isSep);

// Split raw text into [{type:'text',text} | {type:'table',header,rows}] —
// null when no separator-confirmed table exists (caller falls back).
export function splitPipeTables(raw) {
  if (!hasPipeTable(raw)) return null;
  const segs = [];
  let textBuf = [];
  let rowBuf = [];
  let sawSep = false;
  const flushText = () => {
    const t = textBuf.join('\n').trim();
    if (t) segs.push({ type: 'text', text: t });
    textBuf = [];
  };
  const flushTable = () => {
    if (!rowBuf.length) return;
    if (sawSep) {
      const header = rowBuf.length > 1 ? rowBuf[0] : null;
      segs.push({ type: 'table', header, rows: header ? rowBuf.slice(1) : rowBuf });
    } else {
      // pipe lines with no --- separator: not a table, keep as text
      textBuf.push(...rowBuf.map((cells) => `| ${cells.join(' | ')} |`));
    }
    rowBuf = [];
    sawSep = false;
  };
  for (const line of raw.split('\n')) {
    if (isRow(line)) {
      flushText();
      if (isSep(line)) { sawSep = true; continue; }
      rowBuf.push(line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim()));
    } else {
      flushTable();
      textBuf.push(line);
    }
  }
  flushTable();
  flushText();
  return segs.some((s) => s.type === 'table') ? segs : null;
}

const CELL_STYLE = {
  border: '1.5px solid #475569',
  padding: '5px 18px',
  textAlign: 'center',
  fontSize: '0.95em',
};

export function PipeTable({ header, rows, center }) {
  return (
    <div style={{ overflowX: 'auto', margin: center ? '10px auto' : '10px 0', display: 'flex', justifyContent: center ? 'center' : 'flex-start' }}>
      <table style={{ borderCollapse: 'collapse', background: '#fff' }}>
        {header && (
          <thead>
            <tr>
              {header.map((c, i) => (
                <th key={i} style={{ ...CELL_STYLE, fontWeight: 600 }}><Latex>{c}</Latex></th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => <td key={j} style={CELL_STYLE}><Latex>{c}</Latex></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Drop-in block renderer: tables as PipeTable, everything between as the
// usual LatexRenderer. Returns null when the text has no table.
// Bluebook layout (Sandy 2026-08-10): a LONE table sits ABOVE its text, same
// as the SVG figures. Stems carrying several tables (A./B./C./D. inline)
// keep authored order — reordering would orphan the letters.
export function PipeTableBlocks({ text, center }) {
  const segs = splitPipeTables(text);
  if (!segs) return null;
  const tables = segs.filter((s) => s.type === 'table');
  const ordered = tables.length === 1
    ? [tables[0], ...segs.filter((s) => s.type !== 'table')]
    : segs;
  return (
    <div>
      {ordered.map((s, i) => s.type === 'table'
        ? <PipeTable key={i} header={s.header} rows={s.rows} center={center} />
        // block wrapper: LatexRenderer output is inline, so two adjacent text
        // segments (the norm once a lone table is hoisted above them) would
        // otherwise fuse mid-sentence: "...chess club.How many..."
        : <div key={i} className={i ? 'mt-2' : ''}><LatexRenderer>{s.text}</LatexRenderer></div>)}
    </div>
  );
}

export default PipeTableBlocks;
